import { z } from 'zod';
import { ExternalId } from '../ingest/identity.ts';

/**
 * API-triggered Canvas entry (BB-124, CAN-002): the VCG backend enters listed users into a Canvas now, each with its
 * own context (`{{context.${...}}}` in its messages), or a whole segment. `Idempotency-Key` is required; each entry's
 * source key is `api:<key>:<externalId>`, so a retried request enters no one twice (they come back as `duplicate`).
 */
export const CANVAS_TRIGGER_MAX_RECIPIENTS = 100;
export const CANVAS_CONTEXT_MAX_BYTES = 50 * 1024;

const Json = z.record(z.string(), z.unknown());
const sizeOk = (value: unknown) => new TextEncoder().encode(JSON.stringify(value ?? {})).length <= CANVAS_CONTEXT_MAX_BYTES;
const Context = Json.optional().refine(sizeOk, `context is larger than ${CANVAS_CONTEXT_MAX_BYTES / 1024} KB`);

export const CanvasTriggerRecipient = z.strictObject({
  externalId: ExternalId,
  /** Merged over the shared context; ≤ 50 KB together. */
  context: Context,
  /** Applied as trusted attributes before the entry (DEC-013). */
  attributes: Json.optional(),
  /** Unknown users are skipped (`not_found`) unless false — then the profile is created. */
  sendToExistingOnly: z.boolean().default(true),
});
export type CanvasTriggerRecipient = z.infer<typeof CanvasTriggerRecipient>;

export const CanvasTriggerBody = z
  .strictObject({
    recipients: z.array(CanvasTriggerRecipient).min(1).max(CANVAS_TRIGGER_MAX_RECIPIENTS).optional(),
    /** Enter a segment instead of listed recipients. */
    audience: z.strictObject({ segmentApiId: z.uuid() }).optional(),
    /** Shared context of every entry of the request. */
    context: Context,
  })
  .refine((b) => Boolean(b.recipients) !== Boolean(b.audience), { message: 'send either recipients or an audience' });
export type CanvasTriggerBody = z.infer<typeof CanvasTriggerBody>;

/** `rejected` carries the entry rule that refused (`audience`, `already_active`, `not_reeligible`, `entry_limit`, …). */
export const CANVAS_RECIPIENT_STATUSES = ['entered', 'duplicate', 'not_found', 'rejected', 'invalid'] as const;

export interface CanvasTriggerResponse {
  /** Per listed recipient; empty for a segment. */
  results: Array<{ externalId: string; status: (typeof CANVAS_RECIPIENT_STATUSES)[number]; reason?: string }>;
  /** Counts for a segment. */
  audience?: { matched: number; entered: number };
}
